import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Filter, Paperclip, Search } from 'lucide-react'
import { useMeta, useSubmissions, useWorkCenters, type SubmissionFilters } from '@/lib/queries'
import { useAuth } from '@/context/AuthContext'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Spinner,
} from '@/components/ui'
import { ExportButton } from '@/components/ExportButton'
import { Hint } from '@/components/Hint'
import { formatRelative } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import { useT } from '@/i18n/I18nProvider'
import type { EnumOption } from '@/types/api'

/**
 * The four status names the chain hint reads out, looked up in `/api/meta`.
 *
 * The sentence lives in the dictionary; the words "Nova" and "Zatvorena" do
 * not, and must not — statuses are the server's in four languages, and a copy
 * of one in a translation file is the drift this whole layer exists to prevent.
 * So the hint holds placeholders, they are filled here, and if `meta` has not
 * arrived the hint is not shown at all rather than shown with holes in it.
 *
 * SubmissionDetail.tsx carries the same lookup, which is deliberate and
 * not laziness: exporting it from here would make this module export something
 * other than a component, which costs fast refresh on the whole page for the
 * sake of one `find`. lib/utils.ts is not an option either — `shadcn init`
 * rewrites it, and it has no business knowing about `/api/meta`.
 */
function statusChain(options: EnumOption[] | undefined) {
  const label = (value: string) => options?.find((option) => option.value === value)?.label

  const chain = {
    new: label('NEW'),
    inProgress: label('IN_PROGRESS'),
    resolved: label('RESOLVED'),
    closed: label('CLOSED'),
  }

  return Object.values(chain).every(Boolean) ? (chain as Record<string, string>) : null
}

export function SubmissionsPage() {
  const { user } = useAuth()
  const t = useT()
  const meta = useMeta()
  const workCenters = useWorkCenters()

  /*
   * Initial filters can come from the URL, so the reminder notifications can
   * deep-link to exactly what they described — "3 prijave van roka nemaju
   * zaduženog" has to land on those three, not on the whole inbox.
   */
  const [searchParams] = useSearchParams()
  const linkedOverdue = searchParams.get('overdue') === 'true'
  const linkedUnassigned = searchParams.get('unassigned') === 'true'
  const linkedMine = searchParams.get('mine') === 'true'
  // The manager dashboard's "red čekanja" links here with this set.
  const linkedOpen = searchParams.get('otvorene') === 'true'

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')
  const [workCenterId, setWorkCenterId] = useState('')
  const [openOnly, setOpenOnly] = useState(linkedOpen)
  const [overdue, setOverdue] = useState(linkedOverdue)
  const [unassigned, setUnassigned] = useState(linkedUnassigned)
  const [assignedToMe, setAssignedToMe] = useState(linkedMine)
  const [sort, setSort] = useState<SubmissionFilters['sort']>(
    linkedOverdue ? 'due' : 'newest',
  )
  const [page, setPage] = useState(1)
  // Open the panel when arriving from a link, so it is obvious why the list is
  // narrower than usual and how to widen it.
  const [showFilters, setShowFilters] = useState(
    linkedOverdue || linkedUnassigned || linkedMine,
  )

  // Debounced so typing in the search box does not fire a request per keystroke.
  const debouncedSearch = useDebounced(search, 350)

  // Shared by the list request and the export links, so an export can never
  // silently return a different set of rows than the screen shows.
  const exportParams = {
    search: debouncedSearch || undefined,
    status: status ? [status] : undefined,
    type: type ? [type] : undefined,
    workCenterId: workCenterId || undefined,
    openOnly: openOnly || undefined,
    overdue: overdue || undefined,
    unassigned: unassigned || undefined,
    assignedToId: assignedToMe ? user?.id : undefined,
    sort,
  }

  const query = useSubmissions({ ...exportParams, page, perPage: 20 })

  const chain = statusChain(meta.data?.submissionStatuses)

  function resetToFirstPage<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value)
      setPage(1)
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{t('submissions.list.title')}</h1>
          {/* Every row on this screen carries a status badge and nothing on it
              says what the four words commit anybody to. The hint sits on the
              subtitle rather than on the status filter, which is inside a panel
              most people never open. */}
          <p className="muted flex flex-wrap items-center gap-1.5 text-sm">
            <span>
              {t(
                user?.isManager
                  ? 'submissions.list.subtitle.all'
                  : 'submissions.list.subtitle.mine',
              )}
              {query.data && ` · ${query.data.meta.total}`}
            </span>
            {chain && <Hint side="bottom">{t('submissions.status.chain.hint', chain)}</Hint>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setShowFilters((v) => !v)}>
            <Filter className="size-4" aria-hidden />
            {t('submissions.list.filters')}
          </Button>

          {/* The exports carry the CURRENT filters, so what downloads matches
              what is on screen. The server applies them through the same code
              path as the list, so the two cannot disagree. */}
          <ExportButton path="/exports/submissions.xlsx" format="xlsx" params={exportParams} />
          <ExportButton path="/exports/submissions.pdf" format="pdf" params={exportParams} />
        </div>
      </header>

      <div className="relative">
        <Search className="muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" aria-hidden />
        <Input
          value={search}
          onChange={(e) => resetToFirstPage(setSearch)(e.target.value)}
          placeholder={t('submissions.list.search.placeholder')}
          className="pl-9"
          type="search"
          aria-label={t('submissions.list.search.label')}
        />
      </div>

      {showFilters && (
        <Card className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={status}
            onChange={(e) => resetToFirstPage(setStatus)(e.target.value)}
            aria-label={t('submissions.list.filter.status')}
          >
            <option value="">{t('submissions.list.filter.status.all')}</option>
            {meta.data?.submissionStatuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            value={type}
            onChange={(e) => resetToFirstPage(setType)(e.target.value)}
            aria-label={t('submissions.list.filter.type')}
          >
            <option value="">{t('submissions.list.filter.type.all')}</option>
            {meta.data?.submissionTypes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            value={workCenterId}
            onChange={(e) => resetToFirstPage(setWorkCenterId)(e.target.value)}
            aria-label={t('submissions.list.filter.workCenter')}
          >
            <option value="">{t('submissions.list.filter.workCenter.all')}</option>
            {workCenters.data?.map((center) => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </Select>

          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SubmissionFilters['sort'])}
            aria-label={t('submissions.list.sort')}
          >
            <option value="newest">{t('submissions.list.sort.newest')}</option>
            <option value="oldest">{t('submissions.list.sort.oldest')}</option>
            <option value="priority">{t('submissions.list.sort.priority')}</option>
            <option value="due">{t('submissions.list.sort.due')}</option>
          </Select>

          {/* Checkbox, not a bare <input type="checkbox">: those render at about
              13px, which is the hardest thing on the page to hit with a glove on. */}
          <div className="flex flex-wrap gap-x-5 sm:col-span-2 lg:col-span-4">
            <Checkbox
              checked={openOnly}
              onChange={(e) => resetToFirstPage(setOpenOnly)(e.target.checked)}
              label={t('submissions.list.filter.openOnly')}
            />

            <Checkbox
              checked={overdue}
              onChange={(e) => resetToFirstPage(setOverdue)(e.target.checked)}
              label={t('submissions.list.filter.overdue')}
            />

            {user?.isManager && (
              <>
                <Checkbox
                  checked={unassigned}
                  onChange={(e) => resetToFirstPage(setUnassigned)(e.target.checked)}
                  label={t('submissions.list.filter.unassigned')}
                />

                <Checkbox
                  checked={assignedToMe}
                  onChange={(e) => resetToFirstPage(setAssignedToMe)(e.target.checked)}
                  label={t('submissions.list.filter.assignedToMe')}
                />
              </>
            )}
          </div>
        </Card>
      )}

      {query.isLoading && <Spinner />}

      {query.isError && (
        <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />
      )}

      {query.data && query.data.data.length === 0 && (
        <Card>
          <EmptyState
            title={t('submissions.list.empty.title')}
            description={t('submissions.list.empty.description')}
          />
        </Card>
      )}

      {query.data && query.data.data.length > 0 && (
        <>
          <ul className="space-y-2">
            {query.data.data.map((submission) => (
              <li key={submission.id}>
                <Link to={`/prijave/${submission.id}`} className="card block p-4 transition-colors hover:bg-[var(--surface-sunken)]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {submission.title || submission.typeLabel}
                      </p>
                      <p className="muted mt-0.5 truncate text-xs">
                        {submission.referenceNo} · {submission.workCenter?.name}
                        {submission.createdBy && ` · ${submission.createdBy.name}`}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <Badge color={submission.statusColor}>{submission.statusLabel}</Badge>
                      {submission.isOverdue && (
                        <Badge color="rose">{t('submissions.overdue')}</Badge>
                      )}
                    </div>
                  </div>

                  {submission.description && (
                    <p className="muted mt-2 line-clamp-2 text-sm">{submission.description}</p>
                  )}

                  <div className="muted mt-3 flex flex-wrap items-center gap-3 text-xs">
                    <Badge color={submission.typeColor}>{submission.typeLabel}</Badge>
                    <Badge color={submission.priorityColor}>{submission.priorityLabel}</Badge>
                    {(submission.attachmentCount ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Paperclip className="size-3.5" aria-hidden />
                        {submission.attachmentCount}
                      </span>
                    )}
                    <span>{formatRelative(submission.createdAt)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {query.data.meta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('submissions.list.previous')}
              </Button>
              <span className="muted text-sm">
                {query.data.meta.current_page} / {query.data.meta.last_page}
              </span>
              <Button
                variant="secondary"
                disabled={page >= query.data.meta.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('submissions.list.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
